import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { apiJson, fetchAuthedBlob } from '../api/client.js';
import { Modal } from '../components/Modal.jsx';
import { FormIcon } from '../forms/FormIcon.jsx';
import { FormMenu } from '../forms/FormMenu.jsx';
import { StatusPill } from '../forms/FormBadges.jsx';
import { FormRenderer } from '../forms/FormRenderer.jsx';
import { FormBuildTab } from '../forms/editor/FormBuildTab.jsx';
import { FormSettingsTab } from '../forms/editor/FormSettingsTab.jsx';
import { FormAutomationsTab } from '../forms/editor/FormAutomationsTab.jsx';
import { FormSubmissionsTab } from '../forms/editor/FormSubmissionsTab.jsx';
import { downloadBlob } from '../forms/formSchema.js';
import '../forms/forms.css';

const TABS = [
  { id: 'build', label: 'Build' },
  { id: 'settings', label: 'Settings' },
  { id: 'automations', label: 'Automations' },
  { id: 'submissions', label: 'Submissions' },
];

const EDITABLE = ['title', 'description', 'category', 'audience', 'client_id', 'project_id', 'due_date', 'fields', 'settings', 'automations'];

function toDraft(form) {
  return EDITABLE.reduce((acc, key) => ({ ...acc, [key]: form[key] ?? (key === 'description' ? '' : null) }), {});
}

export function AdminFormEditorPage() {
  const { id, tab: tabParam } = useParams();
  const tab = TABS.some((t) => t.id === tabParam) ? tabParam : 'build';
  const navigate = useNavigate();

  const [form, setForm] = useState(null);
  const [draft, setDraft] = useState(null);
  const [savedSnapshot, setSavedSnapshot] = useState('');
  const [options, setOptions] = useState({ clients: [], projects: [], staff: [], templates: [], ghl_connected: false });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [preview, setPreview] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [submissionCount, setSubmissionCount] = useState(null);

  const applyForm = useCallback((next) => {
    setForm(next);
    const d = toDraft(next);
    setDraft(d);
    setSavedSnapshot(JSON.stringify(d));
    setSubmissionCount(next.submissions_count);
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([apiJson(`/api/admin/forms/${id}`), apiJson('/api/admin/forms/options')])
      .then(([formData, opts]) => {
        if (!alive) return;
        applyForm(formData.form);
        setOptions(opts);
      })
      .catch((e) => alive && setError(e.message))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [id, applyForm]);

  const dirty = useMemo(() => draft !== null && JSON.stringify(draft) !== savedSnapshot, [draft, savedSnapshot]);

  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  const patch = useCallback((changes) => setDraft((cur) => ({ ...cur, ...(typeof changes === 'function' ? changes(cur) : changes) })), []);
  const patchSettings = useCallback(
    (group, changes) => setDraft((cur) => ({ ...cur, settings: { ...cur.settings, [group]: { ...cur.settings[group], ...changes } } })),
    [],
  );

  const flash = (message) => {
    setNotice(message);
    setTimeout(() => setNotice(''), 3500);
  };

  const save = async ({ quiet = false } = {}) => {
    setBusy('save');
    setError('');
    try {
      const data = await apiJson(`/api/admin/forms/${id}`, { method: 'PUT', body: draft });
      applyForm(data.form);
      if (!quiet) flash('Changes saved.');
      return data.form;
    } catch (e) {
      setError(e.message);
      return null;
    } finally {
      setBusy('');
    }
  };

  const publish = async () => {
    if (dirty && !(await save({ quiet: true }))) return;
    setBusy('publish');
    setError('');
    try {
      const data = await apiJson(`/api/admin/forms/${id}/publish`, { method: 'POST' });
      applyForm(data.form);
      flash(data.clients_notified ? `Published — ${data.clients_notified} client contact(s) notified.` : 'Form published.');
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  const setStatus = async (status) => {
    setBusy('status');
    try {
      const data = await apiJson(`/api/admin/forms/${id}/unpublish`, { method: 'POST', body: { status } });
      applyForm(data.form);
      flash(status === 'closed' ? 'Form closed — it no longer accepts responses.' : 'Moved back to draft.');
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  const duplicate = async (as) => {
    try {
      const data = await apiJson(`/api/admin/forms/${id}/duplicate`, { method: 'POST', body: { as } });
      navigate(`/admin/forms/${data.form.id}`);
      flash(as === 'template' ? 'Template created.' : 'Copy created.');
    } catch (e) {
      setError(e.message);
    }
  };

  const copyPublicLink = async () => {
    try {
      await navigator.clipboard.writeText(form.public_url);
      flash('Public link copied.');
    } catch {
      setError('Could not copy the link.');
    }
  };

  const exportCsv = async (query = '') => {
    try {
      const blob = await fetchAuthedBlob(`/api/admin/forms/${id}/submissions/export${query}`);
      const slug = (form.title || 'form').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      downloadBlob(blob, `${slug}-submissions.csv`);
    } catch (e) {
      setError(e.message);
    }
  };

  const destroy = async () => {
    try {
      await apiJson(`/api/admin/forms/${id}`, { method: 'DELETE' });
      navigate('/admin/forms');
    } catch (e) {
      setError(e.message);
      setConfirmDelete(false);
    }
  };

  const goTab = (next) => navigate(next === 'build' ? `/admin/forms/${id}` : `/admin/forms/${id}/${next}`);

  if (loading) {
    return (
      <div className="fm-page">
        <p className="fm-empty">Loading form…</p>
      </div>
    );
  }

  if (!form || !draft) {
    return (
      <div className="fm-page">
        <Link to="/admin/forms" className="fm-back">
          <FormIcon name="back" size={16} /> Back to Forms
        </Link>
        <p className="fm-alert fm-alert--error">{error || 'Form not found.'}</p>
      </div>
    );
  }

  const isTemplate = draft.category === 'template';
  const statusKey = isTemplate ? 'template' : form.status;
  const shareable = !isTemplate && draft.audience !== 'internal';

  return (
    <div className="fm-page fm-editor">
      <Link to="/admin/forms" className="fm-back">
        <FormIcon name="back" size={16} /> Back to Forms
      </Link>

      <header className="fm-editor-head">
        <div className="fm-editor-title">
          <div className="fm-editor-title-row">
            <h1>{draft.title || 'Untitled form'}</h1>
            <StatusPill status={statusKey} />
            {dirty ? <span className="fm-unsaved">Unsaved changes</span> : null}
          </div>
          <p>
            {tab === 'submissions'
              ? `${submissionCount ?? form.submissions_count} submission${(submissionCount ?? form.submissions_count) === 1 ? '' : 's'}`
              : draft.description || 'Add a short description in Settings → General.'}
          </p>
        </div>
        <div className="fm-editor-actions">
          {tab === 'submissions' ? (
            <>
              <button type="button" className="fm-btn fm-btn--outline-blue" onClick={() => exportCsv()}>
                <FormIcon name="download" size={16} /> Export CSV
              </button>
              <button type="button" className="fm-btn fm-btn--outline-danger" onClick={() => setConfirmDelete(true)}>
                <FormIcon name="trash" size={16} /> Delete form
              </button>
            </>
          ) : (
            <>
              <button type="button" className="fm-btn fm-btn--outline-blue" onClick={() => setPreview(true)}>
                <FormIcon name="eye" size={16} /> Preview
              </button>
              <button type="button" className="fm-btn fm-btn--outline-blue fm-btn--wide" onClick={() => save()} disabled={busy !== '' || !dirty}>
                {busy === 'save' ? 'Saving…' : 'Save'}
              </button>
              {!isTemplate ? (
                <button type="button" className="fm-btn fm-btn--blue fm-btn--wide" onClick={publish} disabled={busy !== '' || (form.status === 'active' && !dirty)}>
                  {busy === 'publish' ? 'Publishing…' : form.status === 'active' ? (dirty ? 'Save & update' : 'Published') : 'Publish'}
                </button>
              ) : null}
            </>
          )}
          <FormMenu
            vertical
            label="More form actions"
            items={[
              shareable && form.status !== 'draft' && { label: 'Copy public link', icon: 'url', onClick: copyPublicLink },
              shareable && form.status !== 'draft' && { label: 'Open public page', icon: 'external', onClick: () => window.open(form.public_url, '_blank', 'noopener') },
              { label: 'Duplicate', icon: 'copy', onClick: () => duplicate('copy') },
              !isTemplate && { label: 'Save as template', icon: 'template', onClick: () => duplicate('template') },
              isTemplate && { label: 'Create form from template', icon: 'plus', onClick: () => duplicate('from_template') },
              form.status === 'active' && { label: 'Close form (stop responses)', icon: 'lock', onClick: () => setStatus('closed') },
              form.status !== 'draft' && !isTemplate && { label: 'Move back to draft', icon: 'edit', onClick: () => setStatus('draft') },
              form.status === 'closed' && { label: 'Reopen form', icon: 'send', onClick: publish },
              tab !== 'submissions' && { label: 'Export submissions (CSV)', icon: 'download', onClick: () => exportCsv() },
              { label: 'Delete form', icon: 'trash', danger: true, onClick: () => setConfirmDelete(true) },
            ]}
          />
        </div>
      </header>

      <nav className="fm-tabs" role="tablist" aria-label="Form sections">
        {TABS.map((t) => (
          <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} className={`fm-tab${tab === t.id ? ' is-on' : ''}`} onClick={() => goTab(t.id)}>
            {t.label}
            {t.id === 'submissions' && form.new_submissions_count ? <span className="fm-tab-badge">{form.new_submissions_count}</span> : null}
          </button>
        ))}
      </nav>

      {error ? <p className="fm-alert fm-alert--error">{error}</p> : null}
      {notice ? <p className="fm-alert fm-alert--ok">{notice}</p> : null}

      {tab === 'build' ? <FormBuildTab draft={draft} patch={patch} patchSettings={patchSettings} options={options} /> : null}
      {tab === 'settings' ? <FormSettingsTab form={form} draft={draft} patch={patch} patchSettings={patchSettings} options={options} onCopied={flash} /> : null}
      {tab === 'automations' ? <FormAutomationsTab draft={draft} patch={patch} options={options} goSettings={() => goTab('settings')} /> : null}
      {tab === 'submissions' ? (
        <FormSubmissionsTab
          form={form}
          onCountChange={(count, newCount) => {
            setSubmissionCount(count);
            setForm((cur) => ({ ...cur, new_submissions_count: newCount }));
          }}
          onExport={exportCsv}
          onError={setError}
        />
      ) : null}

      <Modal open={preview} title="Preview" description="This is what respondents will see. Submitting is disabled in preview." onClose={() => setPreview(false)} size="lg">
        <div className="fm-preview-frame">
          <FormRenderer
            key={preview ? 'open' : 'closed'}
            definition={{
              title: draft.title,
              description: draft.description,
              fields: draft.fields,
              submit_label: draft.settings.general.submit_label,
              banner: draft.settings.general.banner,
              max_file_mb: draft.settings.advanced.max_file_mb,
              allowed_file_types: draft.settings.advanced.allowed_file_types,
            }}
          />
        </div>
      </Modal>

      <Modal
        open={confirmDelete}
        title="Delete this form?"
        description={`“${form.title}” and its ${submissionCount ?? form.submissions_count} submission(s), including uploaded files, will be permanently removed.`}
        onClose={() => setConfirmDelete(false)}
        footer={
          <>
            <button type="button" className="fm-btn" onClick={() => setConfirmDelete(false)}>
              Cancel
            </button>
            <button type="button" className="fm-btn fm-btn--danger-solid" onClick={destroy}>
              Delete form
            </button>
          </>
        }
      >
        <p className="muted">This cannot be undone.</p>
      </Modal>
    </div>
  );
}
